import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'

import { mediaQueryUp } from 'components/utils/breakpoint'
import InlineGroup from 'components/Common/InlineGroup'
import config from 'constants/config'
import HandpickedPartnersIcon from 'components/Luxkit/Icons/custom/HandpickedPartnersIcon'
import RefundIcon from 'components/Luxkit/Icons/custom/RefundIcon'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import VipInclusionsIcon from 'components/Luxkit/Icons/custom/VipInclusionsIcon'
import BestPricesIcon from 'components/Luxkit/Icons/custom/BestPricesIcon'
import LineCheckCircleIcon from 'components/Luxkit/Icons/line/LineCheckCircleIcon'
import SevenDaysIcon from 'components/Luxkit/Icons/custom/SevenDaysIcon'
import HotelIcon from 'components/Luxkit/Icons/custom/HotelIcon'
import LineTicketIcon from 'components/Luxkit/Icons/line/LineTicketIcon'
import BinocularsIcon from 'components/Luxkit/Icons/custom/BinocularsIcon'
import BookingIcon from 'components/Luxkit/Icons/custom/BookingIcon'
import ExclusiveAccessIcon from 'components/Luxkit/Icons/custom/ExclusiveAccessIcon'
import FlexibleBookingsIcon from 'components/Luxkit/Icons/custom/FlexibleBookingsIcon'
import BuyNowBookLaterIcon from 'components/Luxkit/Icons/custom/BuyNowBookLaterIcon'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import switchFunc from 'lib/switchFunc'
import { SvgIconColour } from 'components/Luxkit/SvgIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  &.block {
    border-left: 1px solid ${props => props.theme.palette.neutral.five};
    margin-left: -1px;
  }

  ${mediaQueryUp.sm} {
    flex-direction: row;
    justify-content: space-around;
    width: auto;

    &.block {
      flex-direction: column;
    }
  }
`

const TextContainer = styled.div`
  max-width:100%;
  line-height: 1.3;

  &.inline {
    > * {
      margin-top: ${rem(16)};
    }
  }

  ${mediaQueryUp.sm} {
    > * {
      margin-top: ${rem(16)};
    }
  }
`

const ItemWrapper = styled(InlineGroup)`
  display: flex;
  align-items: center;

  > a:last-child {
    margin-top: ${rem(16)};
  }

  &.inline {
    & + & {
      margin-top: ${rem(16)};
    }
  }

  &:not(.inline) {
    flex: 1 1 0%;
    flex-direction: column;
    text-align: center;
    margin-bottom: ${rem(30)};
  }

  ${mediaQueryUp.sm} {
    flex-direction: column;
    text-align: center;
    margin-bottom: 0;


    &.inline {
      flex-direction: row;
      text-align: left;
      height: auto;

      > * > * {
        margin-top: 0;
      }
    }
  }
`

const FindOutMoreButton = styled(TextButton)`
  margin-top: ${rem(16)};
  justify-self: flex-end;
`

type IconRenderer = (size: number, iconType: SvgIconColour) => React.ReactNode

const getHowItWorksIcon = switchFunc<IconRenderer>({
  '7-days': (size = 48, iconType) => <SevenDaysIcon colour={iconType} size={size} />,
  'quality-guaranteed': (size = 48, iconType) => <LineCheckCircleIcon colour={iconType} size={size} />,
  'best-prices': (size = 48, iconType) => <BestPricesIcon colour={iconType} size={size} />,
  'vip-inclusions': (size = 48, iconType) => <VipInclusionsIcon colour={iconType} size={size} />,
  hotel: (size = 48, iconType) => <HotelIcon colour={iconType} size={size} />,
  'flexible-bookings': (size = 48, iconType) => <FlexibleBookingsIcon colour={iconType} size={size} />,
  'handpicked-partner': (size = 48, iconType) => <HandpickedPartnersIcon colour={iconType} size={size} />,
  refund: (size = 48, iconType) => <RefundIcon colour={iconType} size={size} />,
  map: (size = 48, iconType) => <LineMapIcon colour={iconType} size={size} />,
  binoculars: (size = 48, iconType) => <BinocularsIcon colour={iconType} size={size} />,
  tickets: (size = 48, iconType) => <LineTicketIcon colour={iconType} size={size} />,
  'exclusive-access': (size = 48, iconType) => <ExclusiveAccessIcon colour={iconType} size={size} />,
  booking: (size = 48, iconType) => <BookingIcon colour={iconType} size={size} />,
  'buy-now-book-later': (size = 48, iconType) => <BuyNowBookLaterIcon colour={iconType} size={size} />,
  calendarIcon: (size = 48, iconType) => <LineCalendarIcon colour={iconType} size={size} />,
}, () => undefined)

interface HowItWorksItemProps {
  inline?: boolean;
  item: any;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  hideContent?: boolean;
}

export function HowItWorksItem(props: HowItWorksItemProps) {
  const { inline, item, className, onClick, hideContent } = props
  const {
    icon,
    iconSize,
    title,
    content,
    contentSize = 'medium',
    iconType = 'primary',
    light,
    slug,
  } = item

  return (
    <ItemWrapper onClick={onClick} className={cn(className, { inline })} size="sm">
      <div>
        {getHowItWorksIcon(icon)(iconSize, iconType) || icon}
      </div>
      <TextContainer className={cn({ inline })}>
        <Heading colour={light ? 'neutral-eight' : undefined} variant="heading6">{title}</Heading>
        {!hideContent && content && <BodyText colour={light ? 'neutral-eight' : undefined} variant={contentSize}>{content}</BodyText>}
      </TextContainer>
      { slug && <FindOutMoreButton kind="tertiary" to={`/${slug}`}>Find out more</FindOutMoreButton>}
    </ItemWrapper>
  )
}

interface BestPriceProps {
  className?: string;
  iconType?: SvgIconColour;
}

export function BestPrice({ className, iconType = 'primary' }: BestPriceProps) {
  return <Wrapper className={className}>
    <ItemWrapper>
      {getHowItWorksIcon('handpicked-partner')(65, iconType)}
      <TextContainer>
        <Heading variant="heading6" as="h4">{config.bestPriceGuarantee?.title}</Heading>
        <BodyText variant="large">{config.bestPriceGuarantee?.content}</BodyText>
        <TextButton kind="secondary" to="/best-price-guarantee">
          Find out more
        </TextButton>
      </TextContainer>
    </ItemWrapper>
  </Wrapper>
}

interface Props {
  inline?: boolean;
  className?: string;
  items: Array<any>;
  hideContent?: boolean;
}

function HowItWorks({ inline, className, items, hideContent }: Props) {
  return <Wrapper className={cn(className, { block: inline })}>
    {items.map(item => <HowItWorksItem
      key={item.title}
      inline={inline}
      item={item}
      hideContent={hideContent}
    />)}
  </Wrapper>
}

export default HowItWorks
